/**
 * Правая часть шапки: кто вошёл и как выйти.
 *
 * Гостю показываются ссылки входа и регистрации — это единственная точка входа
 * в аутентификацию из интерфейса.
 */

import { useEffect } from 'react'
import { Link } from 'react-router'

import { currentUserKey, useCurrentUser, useLogout } from '@/api/auth'
import { onSessionLost } from '@/api/session'
import { strings } from '@/lib/strings'
import { useQueryClient } from '@tanstack/react-query'

function displayedName(email: string, displayName: string | null): string {
  // Если имя не задано, показываем часть email до @ (docs/05-data-model.md).
  return displayName ?? email.split('@')[0] ?? email
}

export function UserMenu() {
  const queryClient = useQueryClient()
  const { data: user, isPending } = useCurrentUser()
  const logout = useLogout()

  useEffect(
    () =>
      onSessionLost(() => {
        // Сессия оборвалась на стороне сервера (истёк refresh, отозвана
        // цепочка) — интерфейс должен сразу стать гостевым.
        queryClient.setQueryData(currentUserKey, null)
      }),
    [queryClient],
  )

  if (isPending) {
    return <span className="text-sm text-muted">{strings.auth.checking}</span>
  }

  if (!user) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <Link to="/login" className="text-muted hover:text-fg">
          {strings.nav.login}
        </Link>
        <Link to="/register" className="text-accent">
          {strings.nav.register}
        </Link>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-4 text-sm">
      <Link to="/me" className="text-muted hover:text-fg">
        {displayedName(user.email, user.display_name)}
      </Link>
      <button
        type="button"
        onClick={() => {
          logout.mutate()
        }}
        disabled={logout.isPending}
        className="text-muted hover:text-fg disabled:opacity-60"
      >
        {strings.nav.logout}
      </button>
    </div>
  )
}
